import { useRef, useState } from 'react';
import axios from 'axios';
import { Progress } from 'reactstrap';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import PhotoList from '../components/PhotoList';
import withErrorHandler from '../components/withErrorHandler';
import { IMAGE_UPLOAD_LIMIT, IMAGE_MAX_SIZE, VIDEO_MAX_SIZE } from '../config';

const types = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'video/mp4',
  'video/avi',
  'video/mov',
  'video/wmv',
  'video/m4v',
];

const UploadHub = ({ handleError, handleWarning }) => {
  const fileInput = useRef(null);

  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);

  /**
   * Method params can be one of two shortcodes: C or S
   * C: Image upload request from image upload (C)lient
   * S: Image upload request from (S)pectrum
   */
  const { method, clientId } = useParams();

  const uploadImage = (data) => {
    const url = `/api/upload/${method === 'c' ? 'client' : 'spectrum'}`;

    axios
      .post(url, data, {
        headers: {
          'x-requested-by': 'webApp',
          'content-type': 'multipart/form-data',
          clientId,
          userAgent: navigator.userAgent,
        },
        onUploadProgress: (ProgressEvent) => {
          setLoaded((ProgressEvent.loaded / ProgressEvent.total) * 100);
        },
      })
      .then((res) => {
        setImages(res.data.map((image) => ({ ...image, canRotate: method === 'c' })));
      })
      .catch((err) => {
        const errorData = {
          description: 'Upload failed',
          errorData: err,
        };

        handleError(errorData);
      });
  };

  const onChangeHandler = (event) => {
    const { files } = event.target;

    if (maxSelectFile(event) && checkMimeType(event) && checkFileSize(event)) {
      setSelectedFiles(files);
      setLoaded(0);
    }
  };

  const onClickHandler = () => {
    const data = new FormData();

    Array.from(selectedFiles).forEach((file) => {
      data.append('file', file);
    });

    uploadImage(data);
  };

  const checkMimeType = (event) => {
    const { files } = event.target;
    const err = [];

    Array.from(files).forEach((file, i) => {
      if (!types.includes(file.type)) {
        err[i] = `File type not supported.`;
      }
    });

    err.forEach((e) => {
      const errorData = {
        description: 'unsupported file type',
        message: e,
      };

      handleError(errorData);
      event.target.value = null;
    });

    return true;
  };

  const checkFileSize = (event) => {
    const { files } = event.target;

    const err = [];

    Array.from(files).forEach((file, i) => {
      if (file.type.indexOf('image') > -1 && file.size > IMAGE_MAX_SIZE) {
        err[i] = `${file.type} is too large, please pick a smaller file\n`;
      }

      if (file.type.indexOf('video') > -1 && file.size > VIDEO_MAX_SIZE) {
        err[i] = `${file.type} is too large, please pick a smaller file\n`;
      }
    });

    err.forEach((e) => {
      const errorData = {
        description: 'file size error',
        errorData: e,
      };

      handleError(errorData);
      event.target.value = null;
    });

    return true;
  };

  const handleUploadButton = () => {
    fileInput.current.click();
  };

  const maxSelectFile = (event) => {
    const { files } = event.target;
    if (files.length <= IMAGE_UPLOAD_LIMIT) return true;

    const errorData = {
      description: 'file size limit exceeded',
      message: `A maximum of ${IMAGE_UPLOAD_LIMIT} images can be uploaded.`,
    };

    handleError(errorData);
    event.target.value = null;

    return false;
  };

  return (
    <>
      <Header isHub />
      <div className="main">
        <div className="container section-device">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2>Share your images</h2>
              <p>
              Tap the button below and select camera to share a single image. If you want to share multiple images, go to your gallery and select them (maximum selection 3).
              </p>

              <button
                type="button"
                className="btn btn-big btn-primary btn-block upload-button mb-2"
                onClick={handleUploadButton}
              >
                <i className="fa fa-camera" />
                Share image
              </button>
              <input
                type="file"
                ref={fileInput}
                className="d-none"
                multiple
                onChange={onChangeHandler}
              />
              {loaded > 0 && (
                <Progress max="100" color="success" value={loaded}>
                  {Math.round(loaded, 2)}%
                </Progress>
              )}
              {selectedFiles.length > 0 && !loaded && (
                <button
                  type="button"
                  className="btn big-button btn-success btn-block"
                  onClick={onClickHandler}
                >
                  Upload {selectedFiles.length} photo
                  {selectedFiles.length !== 1 ? 's' : ''}
                </button>
              )}
              <br />
            </div>
          </div>
        </div>
        {Array.isArray(images) && images.length > 0 && (
          <div className="container section-device">
            <div className="row">
              <div className="col-md-12">
                <h2>Your uploaded images</h2>
                <p>
                  Images you share with us are confidential and will not be shared for any other
                  purpose.
                </p>
                <PhotoList isMobile files={images} />
              </div>
            </div>
          </div>
        )}
        <div className="container disclaimer">
          We recommend you share this image with your GP or Primary Care Provider when you next see
          them. Images you send us are confidential and will not be shared for any other purpose.
          Tēnā rawa atu koe (thank you very much).
        </div>
      </div>
    </>
  );
};

export default withErrorHandler(UploadHub);
